import { clamp } from '../utils/utils.js';

const packedColorCache = new Map();

function parseColorToRgbTuple(value) {
  if (typeof value !== 'string') return null;
  const color = value.trim();
  if (!color) return null;

  const shortHex = /^#([0-9a-f]{3})$/i.exec(color);
  if (shortHex) {
    const [r, g, b] = shortHex[1].split('');
    return [
      Number.parseInt(`${r}${r}`, 16),
      Number.parseInt(`${g}${g}`, 16),
      Number.parseInt(`${b}${b}`, 16)
    ];
  }

  const longHex = /^#([0-9a-f]{6})$/i.exec(color);
  if (longHex) {
    return [
      Number.parseInt(longHex[1].slice(0, 2), 16),
      Number.parseInt(longHex[1].slice(2, 4), 16),
      Number.parseInt(longHex[1].slice(4, 6), 16)
    ];
  }

  const rgb = /^rgb\(\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})\s*\)$/i.exec(color);
  if (rgb) {
    return [
      clamp(Number.parseInt(rgb[1], 10), 0, 255),
      clamp(Number.parseInt(rgb[2], 10), 0, 255),
      clamp(Number.parseInt(rgb[3], 10), 0, 255)
    ];
  }

  return null;
}

function rgbaFromColor(color, alpha) {
  const tuple = parseColorToRgbTuple(color);
  if (!tuple) return `rgba(122, 167, 255, ${alpha})`;
  return `rgba(${tuple[0]}, ${tuple[1]}, ${tuple[2]}, ${alpha})`;
}

function readableTextColor(color) {
  const tuple = parseColorToRgbTuple(color);
  if (!tuple) return '#ffffff';
  const [r, g, b] = tuple.map((channel) => {
    const value = channel / 255;
    return value <= 0.03928
      ? value / 12.92
      : ((value + 0.055) / 1.055) ** 2.4;
  });
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance > 0.58 ? '#111722' : '#ffffff';
}

// Samples a mesh gradient's stops at t (0..1) and returns a flat hex. Used
// wherever a mesh player needs a single color rather than the gradient itself
// -- heads are drawn translated to the player, so a field-space gradient would
// paint them the wrong hue; sampling at their position gives the same result
// the trail underneath them has.
function sampleGradientStops(stops, t) {
  if (!Array.isArray(stops) || stops.length === 0) return null;
  if (stops.length === 1) return stops[0];
  const position = clamp(Number(t) || 0, 0, 1) * (stops.length - 1);
  const index = Math.min(stops.length - 2, Math.floor(position));
  const frac = position - index;
  const from = parseColorToRgbTuple(stops[index]);
  const to = parseColorToRgbTuple(stops[index + 1]);
  if (!from || !to) return stops[index];
  const channel = (i) => Math.round(from[i] + (to[i] - from[i]) * frac);
  return `#${[channel(0), channel(1), channel(2)]
    .map((v) => clamp(v, 0, 255).toString(16).padStart(2, '0'))
    .join('')}`;
}

export {
  parseColorToRgbTuple,
  readableTextColor,
  rgbaFromColor,
  sampleGradientStops
};
